import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { PageResult } from '../interfaces/page-result';
import { AliquotaIcms } from '../interfaces/aliquota-icms';
import { FatClassTributaria } from '../interfaces/fat-class-tributaria';
import { JwtAuthService } from './auth/jwt-auth.service';

/**
 * Alíquotas de ICMS (fat_aliquotas + cad_estado_trib, schema padrao).
 *
 * A alíquota trafega com os estados dentro dela (`estados`): o PUT grava mestre e detalhe de
 * uma vez, e UF que não for enviada é excluída pela API. Gravar/excluir exige
 * ROLE_FATURAMENTO_WRITE (ou ADMIN/ROOT) — quem barra é o backend.
 */
@Injectable({
  providedIn: 'root'
})
export class AliquotaIcmsService {

  readonly path = '/aliquota/icms';

  readonly pathClassTributaria = '/classTrib';

  /** Cache da tabela de classificações — muda por Nota Técnica, não durante a sessão. */
  private classificacoesTributarias$?: Observable<FatClassTributaria[]>;

  constructor(private http: HttpClient, public jwtAuthService: JwtAuthService) { }

  saveOrUpdate(aliquota: AliquotaIcms): Observable<AliquotaIcms> {
    return this.http.put<AliquotaIcms>(`${this.jwtAuthService.apiUrl + this.path}`, aliquota);
  }

  getItem(id: number): Observable<AliquotaIcms> {
    return this.http.get<AliquotaIcms>(`${this.jwtAuthService.apiUrl + this.path}/${id}`);
  }

  /** Todas as alíquotas, sem os estados — alimenta o combo de alíquota interna do destino. */
  getLista(): Observable<AliquotaIcms[]> {
    return this.http.get<AliquotaIcms[]>(`${this.jwtAuthService.apiUrl + this.path}/lista/get`);
  }

  getItemPage(page?: number, size?: number,
    order?: string, direction?: string): Observable<PageResult<AliquotaIcms>> {
    return this.http.get<PageResult<AliquotaIcms>>(`${this.jwtAuthService.apiUrl + this.path}/page/get/?page=${page}&size=`
      + `${size}&order=${order}&direction=${direction}`);
  }

  getItemPageSearch(page?: number, size?: number, search?: string,
    order?: string, direction?: string): Observable<PageResult<AliquotaIcms>> {
    return this.http.get<PageResult<AliquotaIcms>>(`${this.jwtAuthService.apiUrl + this.path}/page/get/?page=${page}&size=` +
      `${size}&search=${search}&order=${order}&direction=${direction}`);
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(`${this.jwtAuthService.apiUrl + this.path}/${id}`);
  }

  /** Classificações tributárias (ICMS + IBS/CBS), para os combos. Resultado cacheado na sessão. */
  getClassificacoesTributarias(forceRefresh = false): Observable<FatClassTributaria[]> {
    if (!this.classificacoesTributarias$ || forceRefresh) {
      this.classificacoesTributarias$ = this.http
        .get<FatClassTributaria[]>(`${this.jwtAuthService.apiUrl + this.pathClassTributaria}/lista/get/all`)
        .pipe(
          map(lista => lista || []),
          catchError(() => of([] as FatClassTributaria[])),
          shareReplay(1)
        );
    }
    return this.classificacoesTributarias$;
  }

  /**
   * Só as classificações do IBS/CBS (cClassTrib) — são as que têm CST preenchido,
   * já ordenadas pelo código.
   */
  getClassificacoesIbsCbs(forceRefresh = false): Observable<FatClassTributaria[]> {
    return this.getClassificacoesTributarias(forceRefresh).pipe(
      map(lista => lista.filter(c => !!c?.cst).sort((a, b) => `${a?.id}`.localeCompare(`${b?.id}`)))
    );
  }

  /**
   * Só as classificações do ICMS/CSOSN — as que têm codigoricms e não têm CST do IBS/CBS,
   * já ordenadas pelo código.
   */
  getClassificacoesIcms(forceRefresh = false): Observable<FatClassTributaria[]> {
    return this.getClassificacoesTributarias(forceRefresh).pipe(
      map(lista => lista
        .filter(c => !c?.cst && !!c?.codigoricms)
        .sort((a, b) => `${a?.id}`.localeCompare(`${b?.id}`)))
    );
  }

  clearClassificacoesTributariasCache(): void {
    this.classificacoesTributarias$ = undefined;
  }
}
